// Utility Helper Functions

/**
 * Get month name from month number.
 * @param {number} monthNumber - Zero-based month index (0 for January, 11 for December).
 * @returns {string} - Full name of the month.
 */
export function getMonthName(monthNumber) {
    const date = new Date();
    date.setMonth(monthNumber);
    return date.toLocaleString("en-US", { month: "long" });
}

/**
 * Extract file extension from filename.
 * @param {string} filename - Name of the file.
 * @returns {string} - File extension.
 */
export function getExtension(filename) {
    const parts = filename.split(".");
    return parts[parts.length - 1];
}

/**
 * Check if a file is an image based on its extension.
 * @param {string} filename - Name of the file.
 * @returns {boolean} - True if the file is an image, false otherwise.
 */
export function checkImageFile(filename) {
    const ext = getExtension(filename).toLowerCase();
    return ["jpg", "jpeg", "png"].includes(ext);
}

/**
 * Check if a file is a text or document file.
 * @param {string} filename - Name of the file.
 * @returns {boolean} - True if the file is text or document, false otherwise.
 */
export function checkTextFile(filename) {
    const ext = getExtension(filename).toLowerCase();
    return ["txt", "doc", "docx", "pdf"].includes(ext);
}

/**
 * Convert a file to a Base64 string.
 * @param {File} file - File object.
 * @returns {Promise<string>} - A promise that resolves to the Base64 string.
 */
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

/**
 * Debounce a function to delay its execution.
 * @param {function} func - Function to debounce.
 * @param {number} delay - Delay time in milliseconds.
 * @returns {function} - Debounced function.
 */
export function debounce(func, delay) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
}
